import {
  useAdversarialIndicator,
  type AdversarialContext,
} from '../hooks/use-adversarial-indicator';

/**
 * AdversarialBadge — PR-B Fatia 7 (ADR-040 + Caderno cap 17 + Brief 2A V2.3
 * adendo terceiro).
 *
 * Badge discreto exibido próximo ao Composer quando a conversa está em modo
 * adversarial (`Bond.adversarialMode != none`) E o atendente tem a
 * capability `ai-context-adversarial-filter` (Pro+).
 *
 * **Estados visuais:**
 *   - `soft` / `competitive` → amber (atenção, não-crítico).
 *   - `strict` → red (crítico).
 *
 * Em NetComet (todos bonds `adversarialMode=none`) o badge nunca aparece —
 * comportamento canônico esperado pela ficha §274 da Fatia 7.
 *
 * **Motor Neutro B4c-b — a sessão confidencial na tela.** Quando a conversa está em caucus, o
 * badge diz isso; e o CÓDIGO do compartimento aparece **só para quem tem direito ao conteúdo** (o
 * servidor decide — `session.compartment === null` significa «em caucus, sem acesso»). O código é
 * renderizado **VERBATIM**: é opaco, cunhado pelo vínculo, e traduzi-lo seria a plataforma
 * decidindo o significado de um código do tenant.
 *
 * ⚠️ **Residual R-J:** o badge é gated em `adversarialMode !== 'none'`. Se o vínculo for virado
 * para `none` com um caucus aberto, o rótulo DESAPARECE — justamente no estado confuso — enquanto
 * a escrita segue carimbando as mensagens (o fix da B4c). Direção segura (nada vaza), mas é
 * armadilha para quem validar.
 */
export function AdversarialBadge({
  adversarial,
}: {
  adversarial: AdversarialContext | null;
}): JSX.Element | null {
  const { isAdversarial, mode, caucus } = useAdversarialIndicator(adversarial);

  if (!isAdversarial || mode === null) return null;

  /* Fase C Brief 2A r7b — DS canonical (Fase A.1-γ GAP-2 resolved).
     Mapping domain mode → DS variant:
       - strict      → adversarial-badge--strict      (danger visual — token --adversarial-strict-*)
       - competitive → adversarial-badge--competitive (warning visual — token --adversarial-competitive-*)
       - soft        → adversarial-badge--partial     (warning visual — token --adversarial-partial-*)
     Refs: Brief_2A_V23_r7b_Operational_Specification.md §1.1 GAP-2. */
  const dsVariant =
    mode === 'strict'
      ? 'adversarial-badge--strict'
      : mode === 'competitive'
        ? 'adversarial-badge--competitive'
        : 'adversarial-badge--partial';

  /* B4c-b — o rótulo da sessão restrita. `caucus === null` ⇒ nada a dizer (copy de hoje,
     inalterada). Com caucus, o código sai VERBATIM para o titular e vira «sem acesso» para quem
     não tem direito ao conteúdo — o servidor é que decide qual dos dois. */
  const caucusLabel =
    caucus === null
      ? null
      : caucus.compartment !== null
        ? `sessão confidencial · ${caucus.compartment}`
        : 'sessão confidencial · sem acesso';

  const titleParts = [
    `Conversa adversarial (${mode})`,
    caucusLabel,
    'Sugestões IA filtradas conforme visibilidade de bolha.',
  ].filter((p): p is string => p !== null);

  return (
    <span
      className={`adversarial-badge ${dsVariant}`}
      data-testid="adversarial-badge"
      data-mode={mode}
      {...(caucus !== null && {
        'data-caucus': caucus.compartment !== null ? 'disclosed' : 'withheld',
      })}
      title={titleParts.join(' — ')}
      aria-label={titleParts.join(' — ')}
    >
      <span aria-hidden>⚖</span>
      <span>Adversarial</span>
      {caucusLabel !== null && (
        <>
          <span aria-hidden className="adversarial-badge__sep">
            ·
          </span>
          <span>{caucusLabel}</span>
        </>
      )}
    </span>
  );
}
